import {
  CreateFilterTemplateDocument,
  type CreateFilterTemplateMutation,
  type CreateFilterTemplateMutationVariables,
  DeleteFilterTemplateDocument,
  type DeleteFilterTemplateMutation,
  type DeleteFilterTemplateMutationVariables,
  FilterTemplatesDocument,
  type FilterTemplatesQuery,
  type FilterTemplatesQueryVariables,
} from "@src/__generated__/graphql";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { action, computed, makeObservable } from "mobx";
import type { AppStore } from "../AppStore";
import type { BaseStore } from "../BaseStore";

export type FilterModelType = "rfi" | "oi";
export type FilterType = "listing" | "detail";

export class FilterTemplatesStore implements BaseStore {
  appStore: AppStore;

  private fetcher = new FetchHelper<
    FilterTemplatesQuery,
    FilterTemplatesQueryVariables
  >(FilterTemplatesDocument);

  private createMutator = new MutationHelper<
    CreateFilterTemplateMutation,
    CreateFilterTemplateMutationVariables
  >(CreateFilterTemplateDocument);

  private deleteMutator = new MutationHelper<
    DeleteFilterTemplateMutation,
    DeleteFilterTemplateMutationVariables
  >(DeleteFilterTemplateDocument);

  constructor(appStore: AppStore) {
    makeObservable(this);
    this.appStore = appStore;
  }

  @computed
  get isFetching(): boolean {
    return this.fetcher.isLoading.value;
  }

  @computed
  get isSaving(): boolean {
    return this.createMutator.isLoading.value;
  }

  @computed
  get isDeleting(): boolean {
    return this.deleteMutator.isLoading.value;
  }

  @action.bound
  async fetchTemplate(
    modelId: string | undefined,
    modelType: string,
    type?: FilterType,
  ): Promise<FilterTemplatesQuery["filterTemplates"] | undefined> {
    const [data, error] = await this.fetcher.fetch({
      modelId,
      modelType,
      type,
    });
    if (error) return;

    return data.filterTemplates;
  }

  @action.bound
  async saveTemplate(
    name: string,
    modelId: string | undefined,
    modelType: string,
    params: string,
    id?: string,
    type?: FilterType,
    onlyForMe?: boolean,
  ): Promise<CreateFilterTemplateMutation["createFilterTemplate"] | undefined> {
    const [data, error] = await this.createMutator.mutate({
      input: {
        id,
        model_id: modelId,
        model_type: modelType,
        type,
        name,
        params,
        only_for_me: onlyForMe,
      },
    });
    if (error) return;

    return data.createFilterTemplate;
  }

  @action.bound
  async deleteTemplate(id: string): Promise<boolean> {
    const [data, error] = await this.deleteMutator.mutate({ id });
    if (error) return false;

    return data.deleteFilterTemplate;
  }
}
